import { useEffect, useState } from 'react';

/**
 * Generation update signalling.
 *
 * The stream tells the reader that a newer generation exists. It never swaps what is
 * on screen: someone mid-paragraph should not have the text replaced underneath them,
 * so the new generation is offered and applied only when they accept it.
 */

export type SyncStatus = 'current' | 'updating' | 'stale' | 'unavailable';

export interface UpdateState {
  status: SyncStatus;
  activeGenerationId: string | null;
  /** Set when the server has moved on from the generation this session loaded with. */
  availableGenerationId: string | null;
  connected: boolean;
}

export function useGenerationUpdates(loadedGenerationId: string | null): UpdateState {
  const [state, setState] = useState<UpdateState>({
    status: 'current',
    activeGenerationId: loadedGenerationId,
    availableGenerationId: null,
    connected: false,
  });

  useEffect(() => {
    const source = new EventSource('/api/v1/updates', { withCredentials: true });

    source.addEventListener('open', () => setState((prev) => ({ ...prev, connected: true })));
    source.addEventListener('generation', (event) => {
      try {
        const payload = JSON.parse((event as MessageEvent<string>).data) as {
          generation: { generationId: string } | null;
          sync: { status: SyncStatus };
        };
        const activeGenerationId = payload.generation?.generationId ?? null;
        setState((prev) => ({
          status: payload.sync.status,
          activeGenerationId,
          availableGenerationId:
            loadedGenerationId && activeGenerationId && activeGenerationId !== loadedGenerationId
              ? activeGenerationId
              : prev.availableGenerationId,
          connected: true,
        }));
      } catch {
        // A malformed frame must not break the stream or the page.
      }
    });
    source.addEventListener('error', () => setState((prev) => ({ ...prev, connected: false })));

    return () => source.close();
  }, [loadedGenerationId]);

  return state;
}
